import { useState, useEffect } from "react";
import axios from "axios";
import api from "../../../utils/api";
import { businessGroups } from "../../utils/spendingCategories";

// Parse 'YYYY-MM-DD' strings as local dates to avoid UTC shifting
const toLocalDateFromYMD = (input) => {
	if (input instanceof Date) return input;
	if (typeof input === "string") {
		const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
		if (m) {
			const [, y, mo, d] = m;
			return new Date(Number(y), Number(mo) - 1, Number(d));
		}
	}
	return new Date(input);
};

const generateMonthKeys = (startDate, endDate) => {
	const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
	const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
	const keys = [];
	let cursor = new Date(start);
	while (cursor <= end) {
		const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
		keys.push(key);
		cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
	}
	return keys;
};

const manualBusinessOverrides = new Set(["Other", "Projects"]);

// Get project names that should be consolidated under "Projects" business
const projectNames = new Set(businessGroups.Projects?.flatMap((g) => g.categories) || []);

// Normalize category name for case-insensitive matching
const normalizeCategory = (cat) => String(cat || "").trim().toLowerCase();

// Build a lookup map: lowercased category -> original category name from data
// This lets us match businessGroups categories case-insensitively
const buildCategoryLookup = (categoryData) => {
	const lookup = {};
	Object.keys(categoryData).forEach((cat) => {
		lookup[normalizeCategory(cat)] = cat;
	});
	return lookup;
};

const useProcessSpendingData = ({ startDate, endDate, categoriesToExclude } = {}) => {
	const [compiledData, setCompiledData] = useState([]);

	useEffect(() => {
		console.log("🌍 REACT_APP_BACKEND_URL from useEffect:", process.env.REACT_APP_BACKEND_URL);
		console.log("🔐 REACT_APP_API_KEY from useEffect:", process.env.REACT_APP_API_KEY);

		const fetchAndProcess = async () => {
			try {
				const res = await api.get(`/money/lineItems`);
				const rawData = res.data;

				const start = startDate ? toLocalDateFromYMD(startDate) : new Date(2024, 0, 1);
				const end = endDate ? toLocalDateFromYMD(endDate) : new Date(2024, 11, 31);
				const months = generateMonthKeys(start, end);
				let monthsInRange = months.length || 12;
				const excludedSet = Array.isArray(categoriesToExclude) && categoriesToExclude.length > 0 ? new Set(categoriesToExclude.map((c) => String(c).trim().toLowerCase())) : null;
				// If viewing 2025, use YTD fractional months based on days since Jan 1 divided by 30.5
				if (start.getFullYear() === 2025 && end.getFullYear() === 2025) {
					const now = new Date();
					const jan1 = new Date(2025, 0, 1);
					const msPerDay = 24 * 60 * 60 * 1000;
					const daysElapsed = Math.max(0, Math.floor((now - jan1) / msPerDay));
					const fractionalMonths = daysElapsed / 30.5;
					// Bound between 1 and available months to avoid divide-by-zero and overcounts
					monthsInRange = Math.min(months.length, Math.max(1, fractionalMonths));
				}

				const filtered = rawData.filter((item) => {
					const date = toLocalDateFromYMD(item.date);
					const categoryName = String(item.category || "")
						.trim()
						.toLowerCase();
					const isExcluded = excludedSet ? excludedSet.has(categoryName) : false;
					return date >= start && date <= end && !isExcluded;
				});

				// Step 1: Organize by Business -> Category
				const businessMap = {};
				const itemsMap = {};

				filtered.forEach((item) => {
					const { business: rawBusiness, category, transaction, date } = item;
					// Consolidate project businesses (CC24, NAPA, etc.) under "Projects"
					const business = projectNames.has(rawBusiness) ? "Projects" : rawBusiness;
					const d = toLocalDateFromYMD(date);
					const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

					if (!businessMap[business]) {
						businessMap[business] = {};
					}
					if (!businessMap[business][category]) {
						businessMap[business][category] = Array(months.length).fill(0); // monthly totals
					}
					if (!itemsMap[business]) {
						itemsMap[business] = {};
					}
					if (!itemsMap[business][category]) {
						itemsMap[business][category] = [];
					}

					const monthIndex = months.indexOf(monthKey);

					if (monthIndex >= 0) {
						businessMap[business][category][monthIndex] += Number(transaction || 0);
					}
					itemsMap[business][category].push(item);
				});

				// Step 2: Assemble final structure
				let compiled = Object.entries(businessMap).map(([businessName, categoryData]) => {
					const groups = businessGroups[businessName] || [];
					const usedCategories = new Set();
					const catLookup = buildCategoryLookup(categoryData);

					// Special handling if business is manually overridden
					if (manualBusinessOverrides.has(businessName)) {
						const groupedCategories = groups
							.map((group) => {
								const subcategories = [];

								group.categories.forEach((catName) => {
									const normalizedCat = normalizeCategory(catName);
									const actualCatKey = catLookup[normalizedCat];
									if (actualCatKey && categoryData[actualCatKey]) {
										const monthlyTotals = categoryData[actualCatKey];
										const total = monthlyTotals.reduce((sum, val) => sum + val, 0);
										subcategories.push({
											category: actualCatKey,
											monthlyTotals,
											total,
											monthlyAverage: total / monthsInRange,
										});
										usedCategories.add(normalizedCat);
									}
								});

								if (subcategories.length > 0) {
									const groupMonthlyTotals = Array(months.length).fill(0);
									subcategories.forEach((sc) => {
										sc.monthlyTotals.forEach((amt, idx) => {
											groupMonthlyTotals[idx] += amt;
										});
									});
									const groupTotal = groupMonthlyTotals.reduce((sum, val) => sum + val, 0);

									return {
										category: group.name,
										subcategories,
										monthlyTotals: groupMonthlyTotals,
										total: groupTotal,
										monthlyAverage: groupTotal / monthsInRange,
										entries: subcategories.flatMap((sc) => sc.entries || []),
									};
								} else {
									return null;
								}
							})
							.filter(Boolean);

						// Handle ungrouped categories into "Other"
						const otherSubcategories = [];
						Object.entries(categoryData).forEach(([catName, monthArr]) => {
							if (!usedCategories.has(normalizeCategory(catName))) {
								const total = monthArr.reduce((sum, val) => sum + val, 0);
								otherSubcategories.push({
									category: catName,
									monthlyTotals: monthArr,
									total,
									monthlyAverage: total / monthsInRange,
									entries: itemsMap[businessName] && itemsMap[businessName][catName] ? itemsMap[businessName][catName] : [],
								});
							}
						});

						if (otherSubcategories.length > 0) {
							const otherMonthlyTotals = Array(months.length).fill(0);
							otherSubcategories.forEach((sc) => {
								sc.monthlyTotals.forEach((amt, idx) => {
									otherMonthlyTotals[idx] += amt;
								});
							});
							const otherTotal = otherMonthlyTotals.reduce((sum, val) => sum + val, 0);

							groupedCategories.push({
								category: "Other",
								subcategories: otherSubcategories,
								monthlyTotals: otherMonthlyTotals,
								total: otherTotal,
								monthlyAverage: otherTotal / monthsInRange,
								entries: otherSubcategories.flatMap((sc) => sc.entries || []),
							});
						}

						return {
							business: businessName,
							categories: groupedCategories,
							monthsInRange,
						};
					}

					// Normal businesses
					const groupedCategories = groups
						.map((group) => {
							const subcategories = [];

							group.categories.forEach((catName) => {
								const normalizedCat = normalizeCategory(catName);
								const actualCatKey = catLookup[normalizedCat];
								if (actualCatKey && categoryData[actualCatKey]) {
									const monthlyTotals = categoryData[actualCatKey];
									const total = monthlyTotals.reduce((sum, val) => sum + val, 0);
									const subEntries = itemsMap[businessName] && itemsMap[businessName][actualCatKey] ? itemsMap[businessName][actualCatKey] : [];
									const linkItems = subEntries.filter((e) => e && e.link);
									const links = Array.from(new Set(linkItems.map((e) => e.link)));
									subcategories.push({
										category: actualCatKey,
										monthlyTotals,
										total,
										monthlyAverage: total / monthsInRange,
										entries: subEntries,
										linkItems: linkItems,
										links: links,
									});
									usedCategories.add(normalizedCat);
								}
							});

							if (subcategories.length > 0) {
								const groupMonthlyTotals = Array(months.length).fill(0);
								subcategories.forEach((sc) => {
									sc.monthlyTotals.forEach((amt, idx) => {
										groupMonthlyTotals[idx] += amt;
									});
								});
								const groupTotal = groupMonthlyTotals.reduce((sum, val) => sum + val, 0);

								return {
									category: group.name,
									subcategories,
									monthlyTotals: groupMonthlyTotals,
									total: groupTotal,
									monthlyAverage: groupTotal / monthsInRange,
									entries: subcategories.flatMap((sc) => sc.entries || []),
								};
							} else {
								return null;
							}
						})
						.filter(Boolean);

					// Handle ungrouped into "Other"
					const otherSubcategories = [];
					Object.entries(categoryData).forEach(([catName, monthArr]) => {
						if (!usedCategories.has(normalizeCategory(catName))) {
							const total = monthArr.reduce((sum, val) => sum + val, 0);
							const subEntries = itemsMap[businessName] && itemsMap[businessName][catName] ? itemsMap[businessName][catName] : [];
							const linkItems = subEntries.filter((e) => e && e.link);
							const links = Array.from(new Set(linkItems.map((e) => e.link)));
							otherSubcategories.push({
								category: catName,
								monthlyTotals: monthArr,
								total,
								monthlyAverage: total / monthsInRange,
								entries: subEntries,
								linkItems: linkItems,
								links: links,
							});
						}
					});

					if (otherSubcategories.length > 0) {
						const otherMonthlyTotals = Array(months.length).fill(0);
						otherSubcategories.forEach((sc) => {
							sc.monthlyTotals.forEach((amt, idx) => {
								otherMonthlyTotals[idx] += amt;
							});
						});
						const otherTotal = otherMonthlyTotals.reduce((sum, val) => sum + val, 0);

						groupedCategories.push({
							category: "Other",
							subcategories: otherSubcategories,
							monthlyTotals: otherMonthlyTotals,
							total: otherTotal,
							monthlyAverage: otherTotal / monthsInRange,
							entries: otherSubcategories.flatMap((sc) => sc.entries || []),
						});
					}

					return {
						business: businessName,
						categories: groupedCategories,
						monthsInRange,
					};
				});

				// If there is no explicit "Other" business in data, synthesize one by aggregating matching categories across all businesses
				if (!compiled.find((b) => b.business === "Other") && businessGroups.Other) {
					const groups = businessGroups.Other;
					const groupedCategories = groups
						.map((group) => {
							const subcategories = [];
							group.categories.forEach((catName) => {
								const normalizedCat = normalizeCategory(catName);
								// aggregate this category across all businesses
								const aggregatedMonthly = Array(months.length).fill(0);
								let aggregatedEntries = [];
								let displayCatName = catName; // Use original name from businessGroups as fallback
								Object.keys(businessMap).forEach((biz) => {
									const bizCatLookup = buildCategoryLookup(businessMap[biz]);
									const actualCatKey = bizCatLookup[normalizedCat];
									if (actualCatKey && businessMap[biz][actualCatKey]) {
										displayCatName = actualCatKey; // Use actual category name from data
										businessMap[biz][actualCatKey].forEach((amt, idx) => {
											aggregatedMonthly[idx] += amt;
										});
									}
									if (actualCatKey && itemsMap[biz] && itemsMap[biz][actualCatKey]) {
										aggregatedEntries = aggregatedEntries.concat(itemsMap[biz][actualCatKey]);
									}
								});
								const total = aggregatedMonthly.reduce((s, v) => s + v, 0);
								if (total !== 0 || aggregatedEntries.length > 0) {
									subcategories.push({
										category: displayCatName,
										monthlyTotals: aggregatedMonthly,
										total,
										monthlyAverage: total / monthsInRange,
										entries: aggregatedEntries,
									});
								}
							});
							if (subcategories.length > 0) {
								const groupMonthlyTotals = Array(months.length).fill(0);
								subcategories.forEach((sc) => {
									sc.monthlyTotals.forEach((amt, idx) => {
										groupMonthlyTotals[idx] += amt;
									});
								});
								const groupTotal = groupMonthlyTotals.reduce((sum, val) => sum + val, 0);
								return {
									category: group.name,
									subcategories,
									monthlyTotals: groupMonthlyTotals,
									total: groupTotal,
									monthlyAverage: groupTotal / monthsInRange,
									entries: subcategories.flatMap((sc) => sc.entries || []),
								};
							}
							return null;
						})
						.filter(Boolean);
					if (groupedCategories.length > 0) {
						compiled.push({
							business: "Other",
							categories: groupedCategories,
							monthsInRange,
						});
					}
				}

				setCompiledData(compiled);
			} catch (err) {
				console.error(err);
			}
		};

		fetchAndProcess();
	}, [startDate, endDate]);

	return { compiledData };
};

export default useProcessSpendingData;
